import React, { useState } from "react";
import { Lightbulb, ShieldAlert, Scale, Target as TargetIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";

/**
 * Compact, render-saved-data-only explainer for a Bid's win probability.
 *
 * Reads ``bid.win_probability_pct`` and ``bid.win_probability_drivers``
 * (the JSON dict already persisted by the win-probability service).
 * Click the badge to expand into a feature breakdown popover.
 *
 * The full live recompute UI lives in WinProbabilityPanel — this is the
 * lightweight surface we sprinkle wherever a Bid is shown (project list,
 * project header, feed cards).
 */
export default function WinProbExplainer({ bid, compact = false }) {
  const [open, setOpen] = useState(false);
  const pct = bid?.win_probability_pct;
  const drivers = bid?.win_probability_drivers;
  if (pct == null) return null;

  const pctCls = pct >= 70 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
              : pct >= 50 ? "bg-blue-100 text-blue-700 border-blue-200"
              : pct >= 30 ? "bg-amber-100 text-amber-700 border-amber-200"
              : "bg-rose-100 text-rose-700 border-rose-200";

  const features = drivers?.features || [];
  const top = drivers?.top_drivers || [];
  const risks = drivers?.top_risks || [];

  return (
    <span className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${pctCls} hover:opacity-90`}
        title="Click for the breakdown"
      >
        <TargetIcon className="w-3 h-3" />
        Win {pct.toFixed(0)}%
        {!compact && drivers && <span className="text-[10px] opacity-70">· why?</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-md shadow-lg w-80 p-3 text-xs">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-slate-700 flex items-center gap-1">
                <Scale className="w-3.5 h-3.5 text-blue-600" /> Win-prob breakdown
              </p>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                ×
              </button>
            </div>

            {!drivers && (
              <p className="text-slate-400 italic">
                No saved breakdown yet — open the Win-Probability tab and click
                Compute to populate this.
              </p>
            )}

            {features.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {features.map((f) => {
                  // For compliance_risk, score is "lower-is-better" — but the
                  // service inverts before display, so we just render score.
                  const pctW = Math.round((f.score ?? 0) * 100);
                  const cls = pctW >= 70 ? "bg-emerald-500"
                           : pctW >= 50 ? "bg-blue-500"
                           : pctW >= 30 ? "bg-amber-500" : "bg-rose-500";
                  return (
                    <div key={f.name}>
                      <div className="flex items-center justify-between text-[10px] text-slate-600">
                        <span className="font-mono">{f.name}</span>
                        <span className="font-mono">
                          {pctW}% <span className="text-slate-400">·w {f.weight}</span>
                        </span>
                      </div>
                      <div className="bg-slate-100 rounded h-1.5 overflow-hidden">
                        <div className={`h-1.5 ${cls}`} style={{ width: `${pctW}%` }} />
                      </div>
                      {f.rationale && (
                        <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{f.rationale}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {top.length > 0 && (
              <div className="border-t pt-2 mb-2">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1 flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" /> Top drivers
                </p>
                <ul className="space-y-0.5">
                  {top.map((d, i) => (
                    <li key={i} className="text-slate-600">• {d}</li>
                  ))}
                </ul>
              </div>
            )}
            {risks.length > 0 && (
              <div className="border-t pt-2">
                <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold mb-1 flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" /> Risks
                </p>
                <ul className="space-y-0.5">
                  {risks.map((d, i) => (
                    <li key={i} className="text-slate-600">• {d}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}
